import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  Zap,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Upload,
  Cpu,
  BookOpen,
  Eye,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import "./GFSDashboard.css";

const FORM_TYPES = {
  // Original GFS forms
  aof: "Account Opening Form (AOF)",
  redemption: "Redemption (RP)",
  additional_purchase: "Additional Purchase (IUTA/AI)",
  // TopUp Order forms
  topup_manulife_prs: "Manulife PRS Form",
  topup_maybank_booking: "Maybank Booking Report",
  topup_phillip_mutual: "Phillip Mutual Report",
  topup_ifast_orders: "iFAST Cash Orders",
  // TopUp Investment forms
  topup_epf_kwsp: "EPF KWSP 9N",
};

const GFSDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [v1WorkOrders, setV1WorkOrders] = useState([]);
  const [v2WorkOrders, setV2WorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingV1, setLoadingV1] = useState(true);
  const [loadingV2, setLoadingV2] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchV1WorkOrders();
    fetchV2WorkOrders();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/v1/gfs/api/stats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch GFS stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchV1WorkOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/v1/gfs/api/work-orders?limit=5&version=v1", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setV1WorkOrders(data.work_orders || []);
      }
    } catch (err) {
      console.error("Failed to fetch V1 work orders:", err);
    } finally {
      setLoadingV1(false);
    }
  };

  const fetchV2WorkOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/v1/gfs/api/work-orders?limit=5&version=v2", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setV2WorkOrders(data.work_orders || []);
      }
    } catch (err) {
      console.error("Failed to fetch V2 work orders:", err);
    } finally {
      setLoadingV2(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "status-completed",
      processing: "status-processing",
      pending: "status-pending",
      failed: "status-failed",
    };
    return classes[status] || "status-pending";
  };

  const getFormTypeLabel = (type) => FORM_TYPES[type] || type || "-";

  const modelCards = [
    {
      id: "v1",
      name: "GFS V1",
      description: "Textract + Qwen",
      icon: Zap,
      color: "#3b82f6",
      bgColor: "#eff6ff",
      path: "/gfs/v1/work-orders",
      stats: stats?.v1_work_orders || 0,
      features: ["AWS Textract OCR", "Qwen Extraction", "Manual Form Type Selection"],
    },
    {
      id: "v2",
      name: "GFS V2",
      description: "Tesseract + Qwen VL",
      icon: Cpu,
      color: "#8b5cf6",
      bgColor: "#f5f3ff",
      path: "/gfs/v2/work-orders",
      stats: stats?.v2_work_orders || 0,
      features: ["Tesseract OCR", "Qwen VL Vision Model", "Auto Form Detection"],
    },
  ];

  // Work Orders Table Component
  const WorkOrdersTable = ({ title, workOrders, loading, color, bgColor, icon: Icon, viewAllPath, version }) => (
    <div className="gfs-workorders-section">
      <div className="gfs-workorders-header" style={{ borderLeftColor: color }}>
        <div className="gfs-workorders-title">
          <Icon size={20} style={{ color }} />
          <h3>{title}</h3>
          <span className="gfs-workorders-count" style={{ backgroundColor: bgColor, color }}>
            {workOrders.length} recent
          </span>
        </div>
        <button 
          className="gfs-view-all-btn" 
          onClick={() => navigate(viewAllPath)}
          style={{ color }}
        >
          View All <ChevronRight size={16} />
        </button>
      </div>

      <div className="gfs-workorders-table-container">
        <table className="gfs-workorders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>Form Type</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="gfs-table-loading">
                  <RefreshCw size={18} className="spin" /> Loading...
                </td>
              </tr>
            ) : workOrders.length === 0 ? (
              <tr>
                <td colSpan="6" className="gfs-table-empty">
                  No work orders yet. 
                  <button onClick={() => navigate(viewAllPath)} style={{ color }}>
                    Upload your first document
                  </button>
                </td>
              </tr>
            ) : (
              workOrders.map((wo) => (
                <tr 
                  key={wo._id} 
                  onClick={() => navigate(`/gfs/${version}/work-order/${wo._id}`)}
                  className="gfs-workorder-row"
                >
                  <td className="gfs-id-cell">{wo._id?.slice(-8)}</td>
                  <td className="gfs-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 25) || "-"}
                    {wo.filename?.length > 25 && "..."}
                  </td>
                  <td>
                    <span 
                      className="gfs-form-badge" 
                      style={{ backgroundColor: bgColor, color }}
                    >
                      {getFormTypeLabel(wo.form_type)}
                      {wo.form_type_auto_detected && <span className="auto-tag">Auto</span>}
                    </span>
                  </td>
                  <td>
                    <span className={`gfs-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td className="gfs-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button 
                      className="gfs-action-btn view" 
                      title="View Details"
                      onClick={() => navigate(`/gfs/${version}/work-order/${wo._id}`)}
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="gfs-dashboard">
      {/* Welcome Banner */}
      <div className="gfs-welcome-banner">
        <div className="gfs-welcome-content">
          <h1>GFS TA Ops OCR</h1>
          <p>Extract Manulife Account Opening & Investment forms using V1 or V2 pipelines</p>
        </div>
        <div className="gfs-welcome-actions">
          <button className="gfs-welcome-btn" onClick={() => navigate("/gfs/v1/work-orders")}>
            <Upload size={20} />
            Upload V1
          </button>
          <button className="gfs-welcome-btn primary" onClick={() => navigate("/gfs/v2/work-orders")}>
            <Upload size={20} />
            Upload V2
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="gfs-stats-overview">
        <div className="gfs-stat-card total">
          <div className="gfs-stat-icon">
            <FileText size={24} />
          </div>
          <div className="gfs-stat-info">
            <span className="gfs-stat-value">{loading ? "-" : stats?.total_work_orders || 0}</span>
            <span className="gfs-stat-label">Total Work Orders</span>
          </div>
        </div>

        <div className="gfs-stat-card completed">
          <div className="gfs-stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="gfs-stat-info">
            <span className="gfs-stat-value">{loading ? "-" : stats?.by_status?.completed || 0}</span>
            <span className="gfs-stat-label">Completed</span>
          </div>
        </div>

        <div className="gfs-stat-card processing">
          <div className="gfs-stat-icon">
            <Clock size={24} />
          </div>
          <div className="gfs-stat-info">
            <span className="gfs-stat-value">{loading ? "-" : stats?.by_status?.processing || 0}</span>
            <span className="gfs-stat-label">Processing</span>
          </div>
        </div>

        <div className="gfs-stat-card failed">
          <div className="gfs-stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="gfs-stat-info">
            <span className="gfs-stat-value">{loading ? "-" : stats?.by_status?.failed || 0}</span>
            <span className="gfs-stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Model Cards */}
      <h2 className="gfs-section-title">
        <TrendingUp size={20} />
        Extraction Pipelines
      </h2>

      <div className="gfs-model-cards">
        {modelCards.map((model) => (
          <div
            key={model.id}
            className="gfs-model-card"
            style={{ "--model-color": model.color, "--model-bg": model.bgColor }}
            onClick={() => navigate(model.path)}
          >
            <div className="gfs-model-card-header">
              <div className="gfs-model-icon" style={{ backgroundColor: model.bgColor }}>
                <model.icon size={28} style={{ color: model.color }} />
              </div>
              <div className="gfs-model-info">
                <h3>{model.name}</h3>
                <p>{model.description}</p>
              </div>
            </div>

            <div className="gfs-model-card-stats">
              <div className="gfs-model-stat">
                <span className="gfs-model-stat-value">{model.stats}</span>
                <span className="gfs-model-stat-label">Work Orders</span>
              </div>
            </div>

            <div className="gfs-model-card-action">
              <button>
                <Upload size={16} />
                Upload & Extract
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* V1 Work Orders Table */}
      <WorkOrdersTable
        title="V1 Work Orders"
        workOrders={v1WorkOrders}
        loading={loadingV1}
        color="#3b82f6"
        bgColor="#eff6ff"
        icon={Zap}
        viewAllPath="/gfs/v1/work-orders"
        version="v1"
      />

      {/* V2 Work Orders Table */}
      <WorkOrdersTable
        title="V2 Work Orders"
        workOrders={v2WorkOrders}
        loading={loadingV2}
        color="#8b5cf6"
        bgColor="#f5f3ff"
        icon={Cpu}
        viewAllPath="/gfs/v2/work-orders"
        version="v2"
      />

      {/* Quick Info */}
      <div className="gfs-quick-info">
        <div className="gfs-info-card">
          <h4>About GFS V1</h4>
          <p>Uses AWS Textract for OCR and Qwen for extraction. Requires manual form type selection from 9 supported forms.</p>
        </div>
        <div className="gfs-info-card">
          <h4>About GFS V2</h4>
          <p>Uses Tesseract OCR with image preprocessing and Qwen VL vision model. Automatically detects form type without manual selection.</p>
        </div>
        <div className="gfs-info-card">
          <h4>Supported Forms</h4>
          <p>AOF, Redemption, Additional Investment, Manulife PRS, Maybank Booking, Phillip Mutual, iFAST Orders, Corporate Investment, and EPF KWSP forms.</p>
        </div>
      </div>
    </div>
  );
};

export default GFSDashboard;
